export const LOCALES = ["en", "ja", "zh-TW"] as const;

export type AppLocale = (typeof LOCALES)[number];

export const DEFAULT_LOCALE: AppLocale = "en";

export const LOCALE_STORAGE_KEY = "app_locale";

export const LOCALE_META: Record<
  AppLocale,
  { label: string; htmlLang: string; ogLocale: string }
> = {
  en: { label: "EN", htmlLang: "en", ogLocale: "en_US" },
  ja: { label: "JP", htmlLang: "ja", ogLocale: "ja_JP" },
  "zh-TW": { label: "ZH", htmlLang: "zh-Hant", ogLocale: "zh_TW" },
};

export function isAppLocale(
  value: string | null | undefined,
): value is AppLocale {
  return LOCALES.includes(value as AppLocale);
}

export function getLocaleFromPath(pathname: string): AppLocale {
  if (pathname === "/ja" || pathname.startsWith("/ja/")) return "ja";
  if (pathname === "/zh-TW" || pathname.startsWith("/zh-TW/")) return "zh-TW";
  return DEFAULT_LOCALE;
}

/** Strip `/ja` or `/zh-TW` prefix; English paths unchanged. */
export function stripLocalePrefix(pathname: string): string {
  if (pathname === "/ja" || pathname === "/zh-TW") return "/";
  if (pathname.startsWith("/ja/")) {
    const rest = pathname.slice("/ja".length);
    return rest.startsWith("/") ? rest : `/${rest}`;
  }
  if (pathname.startsWith("/zh-TW/")) {
    const rest = pathname.slice("/zh-TW".length);
    return rest.startsWith("/") ? rest : `/${rest}`;
  }
  return pathname || "/";
}

/** Prefix a bare site path with the locale (en has no prefix). */
export function localizePath(
  pathname: string,
  locale: AppLocale,
  search = "",
  hash = "",
): string {
  const bare = stripLocalePrefix(pathname) || "/";
  const localized =
    locale === DEFAULT_LOCALE
      ? bare
      : bare === "/"
        ? `/${locale}`
        : `/${locale}${bare}`;
  return `${localized}${search}${hash}`;
}

export function readStoredLocale(): AppLocale | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(LOCALE_STORAGE_KEY);
    return isAppLocale(raw) ? raw : null;
  } catch {
    return null;
  }
}

export function writeStoredLocale(locale: AppLocale) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // Ignore quota / private mode failures.
  }
}
